import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Single axios instance so every request carries the token + base URL consistently.
const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('client');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/* ---------------- Session helpers ---------------- */

// Pulled from the login response's `user` object, stored in sessionStorage by LoginPage.
export const getStudentUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user'));
  } catch {
    return null;
  }
};

export const getAdmissionId = () => getStudentUser()?.admission_details?.admission_id || null;

/* ---------------- Assignments ---------------- */

export const getAssignments = (admissionId = getAdmissionId()) =>
  api.get(`/api/assignment/student/assignments/`, { params: { admission: admissionId } });

export const getAssignmentDetail = (assignmentId) =>
  api.get(`/api/assignment/assignment/${assignmentId}/`);

export const getAssignmentPdfUrl = (filename) => `${API_BASE_URL}/media/assignments/${filename}`;

export const submitAssignment = (formData) =>
  api.post(`/api/assignment/assignment/submission/save/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Update = same endpoint, POST (not PUT) — payload must include the submission id.
export const updateAssignmentSubmission = (formData) =>
  api.post(`/api/assignment/assignment/submission/save/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const getMySubmissions = (admissionId = getAdmissionId()) =>
  api.get(`/api/assignment/assignment/submissions/`, { params: { admission: admissionId } });

export const getSubmissionDetail = (submissionId) =>
  api.get(`/api/assignment/assignment/submission/${submissionId}/`);

export const deleteSubmission = (submissionId, admissionId = getAdmissionId()) =>
  api.delete(`/api/assignment/submission/delete/${submissionId}/`, { params: { admission: admissionId } });

export const getAssignmentsByCourseBatch = (admissionId = getAdmissionId(), course, batch) =>
  api.get(`/api/assignment/student/assignments/course-batch/`, {
    params: { admission: admissionId, ...(course && { course }), ...(batch && { batch }) },
  });

/* ---------------- Attendance ---------------- */

export const getAttendanceHistory = (admissionId = getAdmissionId(), startDate, endDate) =>
  api.get(`/api/admission/student-attendance/${admissionId}/`, {
    params: {
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    },
  });

export const getAttendanceDashboardCount = (admissionId = getAdmissionId()) =>
  api.get(`/api/admission/attendance/dashboard-count/`, { params: { admission_id: admissionId } });

/* ---------------- Certificates ---------------- */

export const downloadCertificate = (certificateId) =>
  api.get(`/api/admission/certificate/download/${certificateId}/`, { responseType: 'blob' });

export const getCertificatePdfUrl = (certificateId) =>
  `${API_BASE_URL}/media/certificates/pdf/certificate_${certificateId}.pdf`;

export default api;
